import DOMPurify, { Config as DomPurifyConfig } from 'dompurify'
import Handlebars from 'handlebars'
import moment from 'moment'

import { ObjectDataRecord } from '@types'

import { paginateIntoPages } from './paginateGridPages'

const SANITIZE_CONFIG: DomPurifyConfig = {
  ADD_ATTR: [
    'data-print-qr',
    'data-print-barcode',
    'data-field',
    'data-symbology',
    'data-print-record-index',
    'role',
  ],
}

const registerHelpers = (hb: typeof Handlebars) => {
  hb.registerHelper(
    'paginateGrid',
    function (records: ObjectDataRecord[], cols: unknown, rows: unknown) {
      const c = Number(cols)
      const r = Number(rows)

      return paginateIntoPages(records, c, r)
    }
  )

  hb.registerHelper('add', (a: unknown, b: unknown) => Number(a) + Number(b))
  hb.registerHelper('sub', (a: unknown, b: unknown) => Number(a) - Number(b))
  hb.registerHelper('div', (a: unknown, b: unknown) => Number(a) / Number(b))
  hb.registerHelper('eq', (a: unknown, b: unknown) => Number(a) === Number(b))

  hb.registerHelper('formatDateRu', (value: unknown) => {
    if (value === null || value === undefined || value === '') return ''

    const m = moment(value as moment.MomentInput)

    return m.isValid() ? m.format('DD.MM.YYYY') : ''
  })

  hb.registerHelper('formatDecimal2', (value: unknown) => {
    if (value === null || value === undefined || value === '') return ''

    const n = Number(value)

    if (!Number.isFinite(n)) return String(value)

    return n.toFixed(2)
  })
}

const registerPartials = (hb: typeof Handlebars, partials: Record<string, string>) => {
  Object.entries(partials).forEach(([name, src]) => {
    hb.registerPartial(name, src)
  })
}

/** Размеры листа в мм уже с учётом ориентации (как для @page CSS). */
export type PrintDocumentContext = {
  records: ObjectDataRecord[]
  paperMm: { width: number; height: number }
  /** Значения, которые нужны конкретному шаблону печати. */
  templateContext?: Record<string, unknown>
}

/**
 * Компилирует полный печатный документ (.hbs). Сетку, поля и зазоры задаёт автор шаблона / partials.
 * В конфиге print form задаются только формат бумаги и ориентация (`paperMm` считается во вне из них).
 */
export const createPrintDocumentRenderer = (
  documentTemplateSrc: string,
  partials: Record<string, string>
) => {
  const hb = Handlebars.create()
  registerHelpers(hb)
  registerPartials(hb, partials)

  const compiled = hb.compile(documentTemplateSrc)

  return (ctx: PrintDocumentContext): string => {
    const rawHtml = compiled({
      ...(ctx.templateContext ?? {}),
      ...ctx,
    })

    return DOMPurify.sanitize(rawHtml, SANITIZE_CONFIG)
  }
}
